import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Animated, {
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";
import { useAppTheme } from "../../../useAppTheme";
import { AppIcon } from "../../AppIcon";
import { Avatar } from "../../Avatar";
import Box from "../../Box";
import { LegalLinks } from "./LegalLinks";
import { NavRailItem } from "./NavRailItem";

type Props = {
	isCollapsed: boolean;
	activeItemKey?: string;
	privacyNoticeUrl?: string;
	mobileAppTermsUrl?: string;
	hasScrolledContent?: boolean;
	renderFooter?: () => React.ReactNode;
	onPressAccount: () => void;
	username?: string;
};

export const Footer = ({
	isCollapsed,
	activeItemKey,
	privacyNoticeUrl,
	mobileAppTermsUrl,
	hasScrolledContent = false,
	renderFooter,
	onPressAccount,
	username,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const renderAvatar = username
		? ({ size }: { size: number }) => <Avatar username={username} size={size} />
		: undefined;

	const progress = useSharedValue(hasScrolledContent ? 1 : 0);

	useEffect(() => {
		progress.value = withTiming(hasScrolledContent ? 1 : 0, { duration: 200 });
	}, [hasScrolledContent, progress]);

	const animatedShadowOpacityStyle = useAnimatedStyle(() => ({
		opacity: progress.value,
	}));

	return (
		<Box
			gap={12}
			paddingHorizontal={isCollapsed ? 0 : 20}
			paddingBottom={20}
			alignItems={isCollapsed ? "center" : "stretch"}
			backgroundColor={theme.colors.surface}
		>
			{!isCollapsed && (
				<Box
					marginHorizontal={16}
					borderTopWidth={1}
					borderTopColor={theme.colors.surfaceVariant}
				/>
			)}
			<NavRailItem
				collapsed={isCollapsed}
				active={activeItemKey === "account"}
				label={username || t("navbar.account")}
				subtitle={username ? t("navbar.account") : undefined}
				icon={
					renderAvatar ?? ((props) => <AppIcon name="cog-outline" {...props} />)
				}
				activeIcon={
					renderAvatar ?? ((props) => <AppIcon name="cog" {...props} />)
				}
				onPress={onPressAccount}
			/>
			{renderFooter?.()}
			{!isCollapsed && (
				<LegalLinks
					privacyNoticeUrl={privacyNoticeUrl}
					mobileAppTermsUrl={mobileAppTermsUrl}
				/>
			)}
			<Animated.View
				pointerEvents="none"
				style={[
					{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						height: 1,
						backgroundColor: theme.colors.surface,
						shadowColor: theme.colors.shadow,
						shadowOffset: { width: 0, height: -4 },
						shadowOpacity: 0.7,
						shadowRadius: 8,
						elevation: 8,
					},
					animatedShadowOpacityStyle,
				]}
			/>
		</Box>
	);
};
